import { useEffect, useState } from 'react'; 
import { Form, Input, Select, Button, message, Tag, Tooltip, Modal } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BoldOutlined, ItalicOutlined, LinkOutlined, UnorderedListOutlined, 
  OrderedListOutlined, CodeOutlined, PictureOutlined 
} from '@ant-design/icons';
import './index.css';

const { Option } = Select;

const WritePost = ({ onAddPost, onEditPost, loggedInUser }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;
  const [formMounted, setFormMounted] = useState(false);

  // 预设标签和分类
  const availableTags = ['技术', '生活', '学习', '工作', '旅行', '美食', 'React', '前端', '后端'];
  const categories = ['技术博客', '生活随笔', '学习笔记', '旅行攻略', '美食分享', '其他'];

  // 富文本工具栏
  const RichTextToolbar = () => {
    const insertSyntax = async (syntaxType) => {
      const { content } = await form.getFieldsValue(['content']);
      const currentContent = content || '';
      const textarea = document.querySelector('.rich-text-area');
      
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = currentContent.slice(start, end);

      let newContent = currentContent;
      switch (syntaxType) {
        case 'bold':
          newContent = `${currentContent.slice(0, start)}**${selectedText || '加粗文本'}**${currentContent.slice(end)}`;
          break;
        case 'italic':
          newContent = `${currentContent.slice(0, start)}*${selectedText || '斜体文本'}*${currentContent.slice(end)}`;
          break;
        case 'link':
          newContent = `${currentContent.slice(0, start)}[${selectedText || '链接文本'}](https://)${currentContent.slice(end)}`;
          break;
        case 'ul':
          newContent = `${currentContent.slice(0, start)}- ${selectedText || '列表项'}\n${currentContent.slice(end)}`;
          break;
        case 'ol':
          newContent = `${currentContent.slice(0, start)}1. ${selectedText || '有序列表项'}\n${currentContent.slice(end)}`;
          break;
        case 'code':
          newContent = `${currentContent.slice(0, start)}\`${selectedText || '代码'}\`${currentContent.slice(end)}`;
          break;
        case 'image':
          newContent = `${currentContent.slice(0, start)}![图片描述](${selectedText || 'https://'})${currentContent.slice(end)}`;
          break;
        default: break;
      }

      form.setFieldsValue({ content: newContent });
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + newContent.slice(start, end).length;
      }, 0);
    };

    return (
      <div className="rich-text-toolbar">
        <Tooltip title="加粗 (**文本**)">
          <Button type="text" icon={<BoldOutlined />} onClick={() => insertSyntax('bold')} />
        </Tooltip>
        <Tooltip title="斜体 (*文本*)">
          <Button type="text" icon={<ItalicOutlined />} onClick={() => insertSyntax('italic')} />
        </Tooltip>
        <Tooltip title="链接 ([文本](url))">
          <Button type="text" icon={<LinkOutlined />} onClick={() => insertSyntax('link')} />
        </Tooltip>
        <Tooltip title="无序列表 (- 项)">
          <Button type="text" icon={<UnorderedListOutlined />} onClick={() => insertSyntax('ul')} />
        </Tooltip>
        <Tooltip title="有序列表 (1. 项)">
          <Button type="text" icon={<OrderedListOutlined />} onClick={() => insertSyntax('ol')} />
        </Tooltip>
        <Tooltip title="代码 (`代码`)">
          <Button type="text" icon={<CodeOutlined />} onClick={() => insertSyntax('code')} />
        </Tooltip>
        <Tooltip title="图片 (![描述](url))">
          <Button type="text" icon={<PictureOutlined />} onClick={() => insertSyntax('image')} />
        </Tooltip>
      </div>
    );
  };

  // 监听表单挂载
  useEffect(() => {
    setFormMounted(true);
  }, []);

  // 编辑模式：初始化表单数据
  useEffect(() => {
    if (!loggedInUser?.username) {
      message.warning('请先登录！');
      navigate('/auth');
      return;
    }

    if (!isEditMode || !formMounted) return;

    const initEditData = async () => {
      try {
        const storedPosts = localStorage.getItem('blogPosts');
        if (!storedPosts) throw new Error('暂无文章数据');
        
        const existingPosts = JSON.parse(storedPosts);
        const postId = Number(editId);
        const postToEdit = existingPosts.find(post => post.id === postId);

        if (!postToEdit) throw new Error('文章不存在');
        if (postToEdit.author !== loggedInUser.username) {
          message.error('无权限编辑他人文章');
          navigate('/');
          return;
        }

        await form.setFieldsValue({
          title: postToEdit.title || '',
          category: postToEdit.category || '其他',
          summary: postToEdit.summary || '',
          tags: postToEdit.tags || [],
          content: postToEdit.content || ''
        });
        console.log('回显数据：', postToEdit.content);
      } catch (err) {
        message.error(err.message || '初始化编辑数据失败');
        navigate('/');
      }
    };

    const timer = setTimeout(initEditData, 100);
    return () => clearTimeout(timer);
  }, [editId, form, isEditMode, loggedInUser, navigate, formMounted]);

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const postData = {
        id: isEditMode ? Number(editId) : Date.now(),
        title: values.title,
        category: values.category,
        summary: values.summary,
        tags: values.tags,
        content: values.content,
        author: loggedInUser.username,
        createTime: isEditMode 
          ? (() => {
              const storedPosts = localStorage.getItem('blogPosts');
              const existingPosts = storedPosts ? JSON.parse(storedPosts) : [];
              const originalPost = existingPosts.find(p => p.id === Number(editId));
              return originalPost?.createTime || new Date().toLocaleDateString('zh-CN', {
                year: 'numeric', month: '2-digit', day: '2-digit'
              }).replace(/\//g, '-');
            })()
          : new Date().toLocaleDateString('zh-CN', {
              year: 'numeric', month: '2-digit', day: '2-digit'
            }).replace(/\//g, '-'),
        updatedAt: new Date().toISOString()
      };

      const storedPosts = localStorage.getItem('blogPosts');
      const existingPosts = storedPosts ? JSON.parse(storedPosts) : [];
      let updatedPosts;

      if (isEditMode) {
        updatedPosts = existingPosts.map(post => post.id === postData.id ? postData : post);
        onEditPost(postData);
        message.success('文章编辑成功！');
      } else {
        updatedPosts = [postData, ...existingPosts];
        onAddPost(postData);
        message.success('文章发布成功！');
        form.resetFields();
      }

      localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
      navigate('/');
    } catch (error) {
      if (error.errorFields) {
        const firstError = error.errorFields[0];
        message.error(`${firstError.name.join('、')}：${firstError.errors[0]}`);
      } else {
        message.error(`操作失败：${error.message || '未知错误'}`);
      }
    }
  };

  // 取消操作
  const handleCancel = () => {
    const confirmText = isEditMode ? '取消编辑' : '取消发布';
    const contentText = isEditMode ? '已修改的内容将不会保存' : '已输入的内容将不会保存';
    
    form.validateFields(['title', 'content']).then(values => {
      if (values.title || values.content) {
        Modal.confirm({
          title: `确定要${confirmText}吗？`,
          content: contentText,
          okText: '确认',
          cancelText: '取消',
          onOk: () => navigate('/'),
        });
      } else {
        navigate('/');
      }
    }).catch(() => navigate('/'));
  };

  return (
    <div className="write-post-container">
      <div className="write-post-header">
        <h1>{isEditMode ? '编辑文章' : '撰写新文章'}</h1>
      </div>
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{ tags: [], category: '其他' }}
        className="write-post-form"
      >
        <Form.Item
          label="文章标题"
          name="title"
          rules={[{ required: true, message: '文章标题不能为空！' }]}
        >
          <Input placeholder="请输入文章标题" size="large" />
        </Form.Item>
        
        <Form.Item
          label="文章分类"
          name="category"
          rules={[{ required: true, message: '文章分类不能为空！' }]}
        >
          <Select style={{ width: '100%' }} placeholder="请选择文章分类">
            {categories.map(category => (
              <Option key={category} value={category}>{category}</Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item
          label="文章摘要"
          name="summary"
          rules={[
            { required: true, message: '文章摘要不能为空！' },
            { max: 200, message: '摘要不能超过200字！' }
          ]}
        >
          <Input.TextArea
            placeholder="请输入文章摘要（不超过200字）"
            autoSize={{ minRows: 2, maxRows: 4 }}
            showCount
            maxLength={200}
          />
        </Form.Item>
        
        <Form.Item
          label="标签"
          name="tags"
          rules={[
            { required: true, message: '请选择至少一个标签！' },
            { type: 'array', message: '标签格式错误！' }
          ]}
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="选择或输入标签（按回车/逗号确认）"
            allowClear
            tokenSeparators={[',', '，', ' ']}
          >
            {availableTags.map(tag => (
              <Option key={tag} value={tag}>
                <Tag color="blue">{tag}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        {/* 修复：用div包裹RichTextToolbar和Input.TextArea，解决Form.Item多子元素问题 */}
        <Form.Item
          label="文章内容（支持Markdown）"
          name="content"
          rules={[{ required: true, message: '文章内容不能为空！' }]}
        >
          <div className="content-wrapper">
            <RichTextToolbar />
            <Input.TextArea
              className="rich-text-area"
              placeholder="支持Markdown语法，使用工具栏快速插入格式"
              autoSize={{ minRows: 10, maxRows: 20 }}
              showCount
              maxLength={10000}
              style={{ marginTop: 8 }}
              onChange={(e) => form.setFieldsValue({ content: e.target.value })}
            />
          </div>
        </Form.Item>
        
        <Form.Item className="form-buttons">
          <Button type="primary" size="large" onClick={handleSubmit}>
            {isEditMode ? '保存修改' : '发布文章'}
          </Button>
          <Button size="large" onClick={handleCancel} style={{ marginLeft: 12 }}>
            取消
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default WritePost;